import { HubConnection, IStreamResult } from "@microsoft/signalr"

export type UnregisterHandler = () => void;
export const ApiHash: string = null;

export class SinglegameServerHub {
    constructor(protected connection: HubConnection) {
    }




    interfaceVersion(): Promise<string> {
        return this.connection.invoke("InterfaceVersion");
    }

    load(loadRequest: LoadRequestDTO): Promise<ApiResultOfLoadResponseDTO> {
        return this.connection.invoke("Load", loadRequest);
    }

    frontendError(message: string): Promise<Void> {
        return this.connection.invoke("FrontendError", message);
    }

    frontendLoadingProgress(progress: string): Promise<void> {
        return this.connection.invoke("FrontendLoadingProgress", progress);
    }

    frontendResources(resources: ResourceMeta[]): Promise<void> {
        return this.connection.invoke("FrontendResources", resources);
    }

    frontendLoadingComplete(autheticatedRequest: AuthenticatedRequestOfLoadingCompleteRequest): Promise<ApiResult> {
        return this.connection.invoke("FrontendLoadingComplete", autheticatedRequest);
    }

    ping(): Promise<void> {
        return this.connection.invoke("Ping");
    }

    keyboard(key: string, pressed: boolean): Promise<void> {
        return this.connection.invoke("Keyboard", key, pressed);
    }

    spin(authenticatedSpinRequest: AuthenticatedRequestOfSpinRequest): Promise<SpinResult> {
        return this.connection.invoke("Spin", authenticatedSpinRequest);
    }

    gameFinished(request: AuthenticatedRequest): Promise<void> {
        return this.connection.invoke("GameFinished", request);
    }

    doubleUp_Start(): Promise<IDoubleUpAction> {
        return this.connection.invoke("DoubleUp_Start");
    }

    doubleUp_Pick(cardColor: CardColor): Promise<DoubleUpPickResult> {
        return this.connection.invoke("DoubleUp_Pick", cardColor);
    }

    doubleUp_CollectHalf(): Promise<IDoubleUpAction> {
        return this.connection.invoke("DoubleUp_CollectHalf");
    }

    doubleUp_CollectAll(): Promise<IDoubleUpAction> {
        return this.connection.invoke("DoubleUp_CollectAll");
    }

    doubleUp_End(): Promise<IDoubleUpAction> {
        return this.connection.invoke("DoubleUp_End");
    }

    getRedundantData(): Promise<RedundantData> {
        return this.connection.invoke("GetRedundantData");
    }

    betChanged(betChangedRequest: AuthenticatedRequestOfBetChangedRequest): Promise<ApiResult> {
        return this.connection.invoke("BetChanged", betChangedRequest);
    }

    payout(): Promise<void> {
        return this.connection.invoke("Payout");
    }

    jpShowStart(p: JpShowParams): Promise<void> {
        return this.connection.invoke("JpShowStart", p);
    }

    gameExit(): Promise<void> {
        return this.connection.invoke("GameExit");
    }


    protected operationHandlers: Map<string, any> = new Map<string, any>();
    public registerOperation<T extends SinglegameServerOperationName>(operation: T, handlerName: string, handler: SinglegameServerOperationHandler<T>): UnregisterHandler {
        let operationHandlerName = `${operation}=>${handlerName}`;

        if (this.operationHandlers.has(operationHandlerName))
        {
            throw `Handler ${handlerName} for operation ${operation} already set`;
        }

        let safeSignalRHandler = (...args: any[]) =>
        {
            try
            {
                (handler as any).apply(null, args);
            }
            catch (ex)
            {
                console.error("Error in signalR handler", ex);
            }
        }

        this.operationHandlers.set(operationHandlerName, safeSignalRHandler);
        this.connection.on(operation, safeSignalRHandler);

        let unregisterHandler = () => {
            this.unregisterOperation(operation, handlerName);
        };
        
        return unregisterHandler;
    }

    public unregisterOperation<T extends SinglegameServerOperationName>(operation: T, handlerName: string) {
        let operationHandlerName = `${operation}=>${handlerName}`;
        let handler = this.operationHandlers.get(operationHandlerName)
        if (handler)
        {
            this.operationHandlers.delete(operationHandlerName);
            this.connection.off(operation, handler);
        }
    }
}

export interface SinglegameServerHandlers {
    "JackpotValuesUpdate": (p: JackpotValuesUpdateParams) => void;
    "Message": (message: MessageDTO) => void;
}

export type SinglegameServerOperationName = keyof SinglegameServerHandlers;
export type SinglegameServerOperationHandler<T extends SinglegameServerOperationName> = SinglegameServerHandlers[T];

export interface ISimpleBonusConfiguration {
}

export interface IFeatureConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
}

export interface IFeatureResult {
    FeatureName: string | undefined;
    FeatureTriggered: boolean;
    Count: number;
}

export interface IFeaturePublicType {
}

export interface ISimpleBonusAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export enum SimpleBonusPhase {
    Start = "Start",
    Run = "Run",
    Spin = "Spin",
    Pick = "Pick",
    End = "End",
}

export interface SimpleSlotGameWinAmounts {
    PaytableWin: Money | undefined;
    SequentialProgressiveNonProgressiveWins: ProgressiveWin[] | undefined;
    TotalWin: Money | undefined;
}

export interface Money {
    InCents: number;
}

export interface ProgressiveWin {
    Level: JackpotLevel;
    ProgressiveIndex: number;
    ProgressiveType: ProgressiveType;
    Win: Money | undefined;
    ExceedsWinLimit: boolean;
    WinTimeUTC: Date;
}

export enum JackpotLevel {
    Jp4 = "jp4",
    Jp4s = "jp4s",
    Jp3 = "jp3",
    Jp3s = "jp3s",
    Jp2 = "jp2",
    Jp1 = "jp1",
}

export enum ProgressiveType {
    Progressive = "Progressive",
    NonProgressive = "NonProgressive",
}

export interface ISimpleBonusPickAction {
}

export interface ISimpleBonusStartAction {
}

export interface ISimpleBonusEndAction {
}

export interface ISimpleBonusSpinAction {
    SpinWithFeatures: SlotGameSpin | undefined;
    Win: SlotGameWin | undefined;
    Triggers: SimpleTriggerResult[] | undefined;
}

export interface SlotGameSpin {
    ReelStops: number[] | undefined;
    Bet: SlotGameBet | undefined;
    FeatureResults: { [key: string]: IFeatureResult; } | undefined;
    ExpectedSymbols: string[][] | undefined;
    ReelRowExpectedSymbols: string[][] | undefined;
}

export interface SlotGameBet {
    Denom: Money | undefined;
    Lines: number;
    TotalBetCredits: number;
    TotalBetCents: Money | undefined;
    BuyBonusCredits: number | undefined;
    BuyBonusMultiplier: number | undefined;
    BuyBonusCents: Money | undefined;
}

export interface SlotGameWin {
    TotalWin: Money | undefined;
    ResultLines: LineWin[] | undefined;
    ScaterWin: Money | undefined;
}

export interface LineWin {
    LineIndex: number;
    SymbolIndex: number;
    SymbolID: string | undefined;
    SymbolCount: number;
    Hit: boolean;
    Win: Money | undefined;
    WildCount: number;
}

export interface SimpleTriggerResult {
    symbolsTriggered: SymbolPosition[] | undefined;
    Bonus: string | undefined;
    WillTrigger: boolean;
    Anticipation: number;
    Count: number;
    Hint: SimplePublicTriggerHint | undefined;
}

export interface SymbolPosition {
    Reel: number;
    Row: number;
}

export interface SimplePublicTriggerHint {
    Multiplier: number | undefined;
    LevelPercent: number;
}

export interface IGameConfiguration {
    GameType: GameType;
    GameName: string | undefined;
    GameIndex: number;
    Order: number;
}

export enum GameType {
    Slot = "Slot",
    DoubleUp = "DoubleUp",
}

export enum SpeedType {
    Type1 = "Type1",
    Type2 = "Type2",
    Type3 = "Type3",
}

export enum CelebrationType {
    Fast = "Fast",
    Normal = "Normal",
    Slow = "Slow",
}

export interface HardwareSettingsBaseValue {
    Name: string | undefined;
    Value: string | undefined;
}

export interface CurrencyOption {
    Name: string | undefined;
    Short: string | undefined;
    Prefix: string | undefined;
    Suffix: string | undefined;
    DecimalSeparator: string | undefined;
    ThousandsSeparator: string | undefined;
}

export interface DoubleUpGameState {
    GameName: string | undefined;
    ParentGameName: string | undefined;
    BetStart: Money | undefined;
    Denom: Money | undefined;
    CreditStart: Money | undefined;
    TotalBet: Money | undefined;
    TotalWin: Money | undefined;
    CreditEnd: Money | undefined;
    GameSequence: string | undefined;
    Actions: IDoubleUpAction[] | undefined;
    Steps: DoubleUpGameStep[] | undefined;
}

export interface IDoubleUpAction {
    Action: string | undefined;
    PickSequence: number;
    Bet: Money | undefined;
    Collected: Money | undefined;
    TotalBet: Money | undefined;
    Credit: Money | undefined;
    CreditEnd: Money | undefined;
    ForceGameEnd: boolean;
    History: Card[];
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export enum Card {
    C2 = "C2",
    C3 = "C3",
    C4 = "C4",
    C5 = "C5",
    C6 = "C6",
    C7 = "C7",
    C8 = "C8",
    C9 = "C9",
    C10 = "C10",
    CJ = "CJ",
    CK = "CK",
    CQ = "CQ",
    CA = "CA",
    D2 = "D2",
    D3 = "D3",
    D4 = "D4",
    D5 = "D5",
    D6 = "D6",
    D7 = "D7",
    D8 = "D8",
    D9 = "D9",
    D10 = "D10",
    DJ = "DJ",
    DK = "DK",
    DQ = "DQ",
    DA = "DA",
    H2 = "H2",
    H3 = "H3",
    H4 = "H4",
    H5 = "H5",
    H6 = "H6",
    H7 = "H7",
    H8 = "H8",
    H9 = "H9",
    H10 = "H10",
    HJ = "HJ",
    HK = "HK",
    HQ = "HQ",
    HA = "HA",
    S2 = "S2",
    S3 = "S3",
    S4 = "S4",
    S5 = "S5",
    S6 = "S6",
    S7 = "S7",
    S8 = "S8",
    S9 = "S9",
    S10 = "S10",
    SJ = "SJ",
    SK = "SK",
    SQ = "SQ",
    SA = "SA",
}

export interface DoubleUpGameStep {
    Step: string | undefined;
    BetCents: number | undefined;
    WinCents: number | undefined;
    CreditCents: number | undefined;
    CreditEndCents: number | undefined;
    CollectedCents: number | undefined;
    Won: boolean | undefined;
    PickSequence: number | undefined;
}

export interface LoadRequestDTO {
    IsReal: boolean;
    GameName: string | undefined;
    LanguageCode: string | undefined;
    HomeUrl: string | undefined;
    ExtraData: string | undefined;
    Token: string | undefined;
    CurrencyCode: string | undefined;
    CashierUrl: string | undefined;
    RealityCheckTimeout: number | undefined;
    HistoryUrl: string | undefined;
    LoggedInTime: number | undefined;
    RegulationSelfTest: string | undefined;
    RegulationLimits: string | undefined;
    RegulationSelfExclusion: string | undefined;
    BetIndex: number;
}

export interface ApiResultOfLoadResponseDTO extends ApiResult {
    Data: LoadResponseDTO | undefined;
}

export interface LoadResponseDTO {
    SessionToken: string | undefined;
    Balance: Money | undefined;
    Language: string | undefined;
    Currency: CurrencyOption | undefined;
    Settings: SettingsNode | undefined;
    GameConfig: SimpleSlotGameConfiguration | undefined;
    freeRoundsStats: FreeRoundsStats | undefined;
    AllowDoubleUp: boolean;
    doubleupConfig: DoubleUpConfiguration | undefined;
}

export interface SettingsNode {
    ID: string | undefined;
    Name: string | undefined;
    Type: string | undefined;
}

export interface SimpleSlotGameConfiguration {
    Name: string | undefined;
    Index: number;
    Order: number;
    Meta: SlotGameMeta | undefined;
    PlayWindow: PlayWindow;
    EnabledBets: BetConfig[] | undefined;
    Playlines: number[][] | undefined;
    Symbols: Symbol[] | undefined;
    Paytable: IPaytableData | undefined;
    ReelSet: number[][] | undefined;
    Feature: string[] | undefined;
    FeatureConfigurations: IFeatureConfiguration[] | undefined;
    BonusTriggers: IPublicSimpleBonusTrigger[] | undefined;
    BonusConfiguration: { [key: string]: ISimpleBonusCommonConfiguration; } | undefined;
    JackpotEnabled: boolean;
    AvailableJackpots: JackpotLevel[] | undefined;
    Setup: SimpleSlotGameSetup | undefined;
    GameType: GameType;
    GameName: string | undefined;
    GameIndex: number;
}

export interface SlotGameMeta {
    GameName: string | undefined;
    GameId: number;
    Demo: boolean;
    GameVersion: string | undefined;
    TargetRTP: number;
    SourceName: string | undefined;
    SourceMath: string | undefined;
    GeneratedDate: Date;
    SignedDate: Date;
    Signature: string | undefined;
    CalculatedSignature: string | undefined;
    onlineParams: OnlineParams | undefined;
    InterfaceVersion: string | undefined;
}

export interface OnlineParams {
    original: string | undefined;
    settings: string | undefined;
    serverid: string | undefined;
    Nojackpot: boolean;
    Currency: string | undefined;
}

export interface PlayWindow {
    Width: number;
    Height: number;
    NumOfSymbols: number;
}

export interface BetConfig {
    Enabled: boolean;
    BuyBonusEnabled: boolean;
    Bet: SlotGameBet | undefined;
}

export interface Symbol {
    ID: string | undefined;
    SymbolType: SymbolType;
    Index: number;
    SymbolTypeStr: string | undefined;
    IsWild: boolean;
}

export enum SymbolType {
    Regular = "Regular",
    Wild = "Wild",
    Scatter = "Scatter",
    ScatterClassic = "ScatterClassic",
    MysteryWild = "MysteryWild",
    ExpandingWildBulk = "ExpandingWildBulk",
    ExpandingWildReel = "ExpandingWildReel",
    Special = "Special",
}

export interface IPaytableData {
    Type: string | undefined;
    Template: string | undefined;
    PaytableForBetCredits: number;
}

export interface IPublicSimpleBonusTrigger {
    TriggerType: string | undefined;
    Count: number;
    BonusName: string | undefined;
    RequiredSymbols: number[];
    TriggerHintState: TriggerHintState[] | undefined;
}

export interface TriggerHintState {
    Bet: Money | undefined;
    Multiplier: number;
}

export interface ISimpleBonusCommonConfiguration {
}

export interface SimpleSlotGameSetup {
    NewSticker: NewSticker;
}

export enum NewSticker {
    None = "None",
    NewGame = "NewGame",
    NewFeatures = "NewFeatures",
}

export interface FreeRoundsStats {
    FreeRoundsNumber: number;
    PlayedFreeRoundsNumber: number;
    FreeRoundsBet: Money | undefined;
}

export interface DoubleUpConfiguration {
    GameType: GameType;
    GameName: string | undefined;
    GameIndex: number;
    MaxPicks: number;
    Order: number;
}

export interface ApiResult {
    ResultStatus: ResultStatus;
    ErrorMessage: string | undefined;
}

export enum ResultStatus {
    Success = "Success",
    Error = "Error",
    NoGame = "NoGame",
    NoRound = "NoRound",
    AuthenticationError = "AuthenticationError",
    NotAuthenticated = "NotAuthenticated",
    SpinError = "SpinError",
    ReportError = "ReportError",
    NoOperator = "NoOperator",
    CurrencyMismatch = "CurrencyMismatch",
}

export interface Void {
}

export interface ResourceMeta {
    name: string | undefined;
    type: string | undefined;
    paths: string[] | undefined;
}

export interface AuthenticatedRequestOfLoadingCompleteRequest extends AuthenticatedRequest {
    Request: LoadingCompleteRequest | undefined;
}

export interface LoadingCompleteRequest {
    BetIndex: number;
}

export interface AuthenticatedRequest {
    SessionToken: string | undefined;
}

export interface AuthenticatedRequestOfSpinRequest extends AuthenticatedRequest {
    Request: SpinRequest | undefined;
}

export interface SpinRequest {
    BetIndex: number;
    betType: BetType;
}

export enum BetType {
    Normal = "Normal",
    BuyBonus = "BuyBonus",
}

export interface SpinResult extends ApiResultOfSimpleSlotGameState {
    PlayerMessage: StorageMessage | undefined;
}

export interface StorageMessage {
    Title: string | undefined;
    Text: string | undefined;
    Buttons: StorageMessageButton[] | undefined;
}

export interface StorageMessageButton {
    Text: string | undefined;
    Action: string | undefined;
    Link: string | undefined;
    LinkType: string | undefined;
}

export interface ApiResultOfSimpleSlotGameState extends ApiResult {
    Data: SimpleSlotGameState | undefined;
}

export interface SimpleSlotGameState {
    GameName: string | undefined;
    GameSeq: string | undefined;
    CreditStart: Money | undefined;
    CreditEnd: Money | undefined;
    Bet: BetPlayed | undefined;
    Paid: Money | undefined;
    TotalJackpotIncrement: number | undefined;
    FreeRoundsNumber: number | undefined;
    PlayedFreeRoundsNumber: number | undefined;
    FreeRoundsBet: Money | undefined;
    TotalWin: SimpleSlotGameWinAmounts | undefined;
    Actions: ISimpleBonusAction[] | undefined;
    WasBonus: boolean;
    WasHandpay: boolean;
    GameStartData: GameStartData | undefined;
    GameEndData: GameEndData | undefined;
    CanCollect: boolean;
}

export interface BetPlayed {
    Bet: SlotGameBet | undefined;
    BetType: BetType;
}

export interface GameStartData {
    GameName: string | undefined;
    GameSequence: string | undefined;
    Denom: SASDenom | undefined;
    Bet: Money | undefined;
    Paid: Money | undefined;
    MaxBet: Money | undefined;
}

export interface SASDenom {
    code: number;
    cents: number;
    Code: number;
    Cents: number;
    DisplayValue: string | undefined;
}

export interface GameEndData {
    GameName: string | undefined;
    GameIndex: number;
    GameSequence: string | undefined;
    Denom: SASDenom | undefined;
    ReelPosition: number;
    PaytableWin: Money | undefined;
    PaytableWinLimitExceeded: boolean;
    ProgressiveWins: ProgressiveWin[] | undefined;
    NonProgressiveWins: ProgressiveWin[] | undefined;
    TotalWin: Money | undefined;
}

export enum CardColor {
    Red = "Red",
    Black = "Black",
}

export interface DoubleUpPickResult {
    Action: IDoubleUpAction | undefined;
    LastStep: DoubleUpGameStep | undefined;
}

export interface RedundantData {
    CurrentBalance: Money | undefined;
    roundsStats: FreeRoundsStats | undefined;
}

export interface AuthenticatedRequestOfBetChangedRequest extends AuthenticatedRequest {
    Request: BetChangedRequest | undefined;
}

export interface BetChangedRequest {
    BetIndex: number;
}

export interface JpShowParams {
    jackpotName: string | undefined;
    duration: number;
    win: number;
}

export interface JackpotValuesUpdateParams {
    JackpotValues: { [key in keyof typeof JackpotLevel]?: JackpotValue2; } | undefined;
}

export interface JackpotValue2 {
    JackpotLevel: JackpotLevel;
    CurrentValue: number;
    Multiplier: number;
    CurrentMultipliedValue: number;
    ResetValue: number;
    ResetMultipliedValue: number;
}

export interface MessageDTO {
    Title: string | undefined;
    Text: string | undefined;
    DisplayType: string | undefined;
    Buttons: MessageButtonDTO[] | undefined;
}

export interface MessageButtonDTO {
    Text: string | undefined;
    Action: string | undefined;
    Link: string | undefined;
    LinkType: string | undefined;
}

export interface PublicMultiBonusConfig {
    FeatureConfiguration: IFeatureConfiguration[] | undefined;
    MultiFeatureConfiguration: { [key: string]: IFeatureConfiguration; } | undefined;
    MultiReelSet: { [key: string]: PublicReel[]; } | undefined;
    MultiPlayWindow: { [key: string]: PlayWindow; } | undefined;
    TriggerCount: number;
    ResetSpinsCount: number;
}

export interface PublicReel {
    Symbols: number[] | undefined;
    Name: string | undefined;
    Index: number;
    Col: number;
    Row: number;
    ColSpan: number;
    RowSpan: number;
    Height: number;
    StartPosition: number;
}

export interface SpinAndHoldConfiguration {
    PlayWindow: PlayWindow;
    ReelSet: PublicReel[] | undefined;
    ReelSetId: string | undefined;
    SymbolSpanSize: SymbolSpanSize[] | undefined;
    Trigger: IPublicSimpleBonusTrigger | undefined;
    TriggerCount: number;
    ResetSpinsCount: number;
    FeatureConfigurations: IFeatureConfiguration[] | undefined;
    FeatureConfigurationsById: { [key: string]: IFeatureConfiguration; } | undefined;
    Multiplier: number;
}

export interface SymbolSpanSize {
    RowSpan: number;
    ColSpan: number;
}

export interface BigSymbolFreegamesConfiguration {
    PlayWindow: PlayWindow;
    ReelSet: PublicReel[] | undefined;
    ReelSetId: string | undefined;
    FeatureConfigurations: IFeatureConfiguration[] | undefined;
    FeatureConfigurationsById: { [key: string]: IFeatureConfiguration; } | undefined;
    SymbolSpanSizes: SymbolSpanSize[] | undefined;
}

export interface ExtendedSpinAndHoldConfiguration extends SpinAndHoldConfiguration {
}

export interface FreegamesConfiguration {
    StartingOptions: FreegamesOption[] | undefined;
    SimpleBonusTriggers: IPublicSimpleBonusTrigger[] | undefined;
    PlayWindow: PlayWindow;
    ReelSet: number[][] | undefined;
    ReelSetId: string | undefined;
    FeatureConfigurations: IFeatureConfiguration[] | undefined;
    FeatureConfigurationsById: { [key: string]: IFeatureConfiguration; } | undefined;
}

export interface FreegamesOption {
    Symbols: number;
    StartingFreegames: number;
}

export interface ExpandingWildBulkConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    Symbol: string | undefined;
}

export interface ExpandingWildReelConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    Symbol: string | undefined;
}

export interface ScatterConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    Symbols: string[] | undefined;
}

export interface ScatterClassicConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    Symbols: string[] | undefined;
}

export interface ScatterRandomAwardConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    RequiredSymbols: string[];
    FullCount: number;
    AvailableAwards: IAwardOption[] | undefined;
    MadeForBetCredits: number | undefined;
}

export interface IAwardOption {
    AwardType: AwardType;
    AwardIndex: number;
    AwardName: string | undefined;
}

export enum AwardType {
    Jackpot = "Jackpot",
    BetMultiplier = "BetMultiplier",
    Credit = "Credit",
}

export interface PlusOneFeatureConfiguration {
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    RequiredSymbol: string | undefined;
    Value: number;
    MaxAdditionalFreegames: number;
}

export interface UnlockRowsConfig {
    Rows: UnlockRow[] | undefined;
    BaseCount: number;
    FeatureType: string | undefined;
    FeatureName: string | undefined;
    TopRow: number;
    BottomRow: number;
}

export interface UnlockRow {
    Index: number;
    Unlock: number;
}

export interface ExpandingWildBulkResult {
    Positions: Position[] | undefined;
    FeatureName: string | undefined;
    FeatureTriggered: boolean;
    Count: number;
}

export interface Position {
    reel: number;
    row: number;
}

export interface ExpandingWildReelResult {
    Positions: Position[] | undefined;
    FeatureName: string | undefined;
    FeatureTriggered: boolean;
    Count: number;
}

export interface ScatterResult {
    FeatureTriggered: boolean;
    RequiredSymbols: Symbol[] | undefined;
    PositionsPerSymbol: { [key: string]: ScatterWin; } | undefined;
    Count: number;
    FeatureName: string | undefined;
}

export interface ScatterWin {
    Symbol: number;
    SymbolID: string | undefined;
    Count: number;
    Win: Money | undefined;
    Position: Position[] | undefined;
}

export interface ScatterClassicResult {
    FeatureTriggered: boolean;
    RequiredSymbols: Symbol[] | undefined;
    PositionsPerSymbol: { [key: string]: ScatterWin; } | undefined;
    Count: number;
    FeatureName: string | undefined;
}

export interface ScatterRandomAwardResult {
    FeatureTriggered: boolean;
    ScattersAddedThisSpin: number;
    ScattersTotal: number;
    FullCountRequired: number;
    Count: number;
    ScatterColumnRow: IAwardOption[] | undefined;
    NumScattersPerReel: number[] | undefined;
    ScattersPosition: AwardOptionPosition[] | undefined;
    ScattersPositionThisSpin: AwardOptionPosition[] | undefined;
    FullAwardOption: IAwardOption | undefined;
    FullAward: Award | undefined;
    Awards: AwardPositionWithValue[] | undefined;
    FeatureName: string | undefined;
}

export interface AwardOptionPosition {
    Row: number;
    Col: number;
    Reel: number;
    ReelOffset: number;
    ReelPosition: number;
    RowSpan: number;
    ColSpan: number;
    AwardOption: IAwardOption | undefined;
}

export interface Award {
    AwardOption: IAwardOption | undefined;
    Value: Money | undefined;
}

export interface AwardPositionWithValue {
    Value: Money | undefined;
    AwardOptionPosition: AwardOptionPosition | undefined;
}

export interface PlusOneFeatureResult {
    AdditionalFreegames: number;
    SymbolPositions: SymbolPosition[] | undefined;
    FeatureTriggered: boolean;
    Count: number;
    FeatureName: string | undefined;
}

export interface UnlockRowsResult {
    Rows: UnlockRow[] | undefined;
    FeatureTriggered: boolean;
    Count: number;
    CountTotal: number;
    FeatureName: string | undefined;
}

export interface BetMultiplierAwardOption {
    AwardType: AwardType;
    AwardName: string | undefined;
    AwardIndex: number;
    BetMultiplier: number;
}

export interface CreditAwardOption {
    AwardType: AwardType;
    AwardName: string | undefined;
    AwardIndex: number;
    Credits: number;
    MadeForBetCredits: number;
}

export interface JackpotAwardOption {
    AwardType: AwardType;
    AwardName: string | undefined;
    AwardIndex: number;
    JackpotName: string | undefined;
    JackpotLevel: JackpotLevel;
}

export interface BasegameSpinAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    Spin: SlotGameSpin | undefined;
    SpinWithFeatures: SlotGameSpin | undefined;
    Triggers: SimpleTriggerResult[] | undefined;
    Win: SlotGameWin | undefined;
    AnticipationReel: number;
}

export interface DoubleUpCollectAllAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    UsedCards: Card[];
    History: Card[];
    Phase: SimpleBonusPhase;
    Depth: number;
    ForceGameEnd: boolean;
    PickSequence: number;
    Bet: Money | undefined;
    Collected: Money | undefined;
    TotalBet: Money | undefined;
    Credit: Money | undefined;
    CreditEnd: Money | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface DoubleUpCollectHalfAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    PickSequence: number;
    UsedCards: Card[];
    History: Card[];
    Phase: SimpleBonusPhase;
    Bet: Money | undefined;
    Collected: Money | undefined;
    TotalBet: Money | undefined;
    Credit: Money | undefined;
    CreditEnd: Money | undefined;
    Depth: number;
    ForceGameEnd: boolean;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface DoubleUpEndAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    PickSequence: number;
    History: Card[];
    Credit: Money | undefined;
    Bet: Money | undefined;
    Collected: Money | undefined;
    CreditEnd: Money | undefined;
    TotalBet: Money | undefined;
    BetStart: Money | undefined;
    ForceGameEnd: boolean;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface DoubleUpPickAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    PickSequence: number;
    PlayerSelectedColor: CardColor;
    RandomCard: Card;
    RandomCardColor: CardColor;
    Won: boolean;
    UsedCards: Card[];
    History: Card[];
    Phase: SimpleBonusPhase;
    Depth: number;
    Bet: Money | undefined;
    Collected: Money | undefined;
    TotalBet: Money | undefined;
    Credit: Money | undefined;
    CreditEnd: Money | undefined;
    ForceGameEnd: boolean;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface DoubleUpStartAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    PickSequence: number;
    History: Card[];
    Bet: Money | undefined;
    Collected: Money | undefined;
    TotalBet: Money | undefined;
    Credit: Money | undefined;
    CreditEnd: Money | undefined;
    ForceGameEnd: boolean;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface SpinAndHoldEndAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Multiplier: number;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    FullAward: Award | undefined;
    Awards: AwardPositionWithValue[] | undefined;
}

export interface SpinAndHoldSpinAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Multiplier: number;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    Win: SlotGameWin | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    SpinsRemaining: number;
    SpinsPlayed: number;
    SpinsAdditional: number;
    LimitingReel: number;
    HoldReels: number[] | undefined;
    SpinWithFeatures: SlotGameSpin | undefined;
    Triggers: SimpleTriggerResult[] | undefined;
}

export interface SpinAndHoldStartAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    PlayWindow: PlayWindow;
    ReelSetId: string | undefined;
    FeatureConfigurationIds: string[] | undefined;
    Types: MultiBonusType[];
    Multiplier: number;
    Win: SlotGameWin | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    Depth: number;
    SpinsStarted: number;
    SpinsRemaining: number;
    LimitingReel: number;
    HoldReels: number[] | undefined;
    ReelStops: number[] | undefined;
    Spin: SlotGameSpin | undefined;
    SpinWithFeatures: SlotGameSpin | undefined;
    Triggers: SimpleTriggerResult[] | undefined;
}

export enum MultiBonusType {
    Double = "Double",
    Jackpot = "Jackpot",
    Multiplier = "Multiplier",
}

export interface ISpinAndHoldAction {
}

export interface BigSymbolFreegamesEndAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
}

export interface BigSymbolFreegamesSpinAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    SpinsStarted: number;
    SpinsPlayed: number;
    SpinsRemaining: number;
    SpinsAdditional: number;
    Spin: SlotGameSpin | undefined;
    SpinWithFeatures: SlotGameSpin | undefined;
    Anticipation: number;
    Triggers: SimpleTriggerResult[] | undefined;
    Win: SlotGameWin | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
}

export interface BigSymbolFreegamesStartAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    SpinsStarted: number;
    ReelStops: number[] | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface BigSymbolSpinAndHoldEndAction extends SpinAndHoldEndAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface BigSymbolSpinAndHoldSpinAction extends SpinAndHoldSpinAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface BigSymbolSpinAndHoldStartAction extends SpinAndHoldStartAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface ExtendedSpinAndHoldEndAction extends SpinAndHoldEndAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface ExtendedSpinAndHoldSpinAction extends SpinAndHoldSpinAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface ExtendedSpinAndHoldStartAction extends SpinAndHoldStartAction {
    Bonus: string | undefined;
    Action: string | undefined;
}

export interface FreegamesEndAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
}

export interface FreegamesSpinAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    SpinsStarted: number;
    SpinsPlayed: number;
    SpinsRemaining: number;
    SpinsAdditional: number;
    SpinWithFeatures: SlotGameSpin | undefined;
    Spin: SlotGameSpin | undefined;
    Anticipation: number;
    Triggers: SimpleTriggerResult[] | undefined;
    Win: SlotGameWin | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
}

export interface FreegamesStartAction {
    Bonus: string | undefined;
    Variant: string | undefined;
    Action: string | undefined;
    Phase: SimpleBonusPhase;
    Depth: number;
    SpinsStarted: number;
    ReelStops: number[] | undefined;
    WinAmounts: SimpleSlotGameWinAmounts | undefined;
    WinSinceGameStart: SimpleSlotGameWinAmounts | undefined;
    WinSinceBonusStart: SimpleSlotGameWinAmounts | undefined;
}

export interface HardwareSettingsValue extends HardwareSettingsBaseValue {
    Type: string | undefined;
    Description: string | undefined;
    DefaultValue: string | undefined;
}

export interface HardwareSettingsEnumValue extends HardwareSettingsValue {
    AvailableValues: EnumItem[] | undefined;
}

export interface EnumItem {
    Value: string | undefined;
    Label: string | undefined;
}